<template>
  <div class="base-input-wrapper">
    <label v-if="label" :for="inputId" class="base-input__label">{{ label }}</label>
    <div class="base-input__container">
      <input
        :id="inputId"
        :type="type"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :min="min"
        :max="max"
        :step="step"
        :required="required"
        class="base-input"
        @input="$emit('update:modelValue', ($event.target as HTMLInputElement).value)"
        @focus="$emit('focus', $event)"
        @blur="$emit('blur', $event)"
      />
      <slot name="suffix"></slot>
    </div>
    <small v-if="hint" class="base-input__hint">{{ hint }}</small>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps<{
  modelValue: string | number;
  label?: string;
  type?: string;
  placeholder?: string;
  hint?: string;
  disabled?: boolean;
  required?: boolean;
  min?: number | string;
  max?: number | string;
  step?: number | string;
}>();

defineEmits<{
  'update:modelValue': [value: string];
  focus: [event: FocusEvent];
  blur: [event: FocusEvent];
}>();

const inputId = computed(() => `input-${Math.random().toString(36).substr(2, 9)}`);
</script>

<style scoped>
.base-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.base-input__label {
  font-size: 0.7rem;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.065em;
  transition: color var(--transition-fast);
}

.base-input-wrapper:focus-within .base-input__label {
  color: var(--color-primary);
}

.base-input__container {
  position: relative;
  display: flex;
  align-items: center;
}

.base-input {
  width: 100%;
  height: 40px;
  padding: 0 0.875rem;
  background: var(--color-bg-secondary);
  border: 1.5px solid transparent;
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
  font-family: var(--font-family);
  color: var(--color-text-primary);
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
  box-sizing: border-box;
  outline: none;
}

.base-input::placeholder {
  color: var(--color-text-muted);
}

.base-input:hover:not(:disabled):not(:focus) {
  border-color: var(--color-border-medium);
}

.base-input:focus {
  background: var(--color-bg-primary);
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(30, 86, 217, 0.12);
}

.base-input:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* date/number inputs: remove extra padding */
.base-input[type="date"],
.base-input[type="number"] {
  padding: 0 0.75rem;
}

.base-input__hint {
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  margin-top: 2px;
}
</style>
